import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { useStore } from "@/store/useStore";
import { useEffect } from "react";
import { BookOpen, Zap, Trophy, Target, Star, Globe } from "lucide-react";
import { playTTS } from "@/lib/utils";

export default function HomePage() {
  const { isAuthenticated } = useStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) navigate("/learn", { replace: true });
  }, [isAuthenticated]);

  const features = [
    { icon: "🎴", title: "Flashcard tương tác", desc: "Học từ vựng qua thẻ 3D với phát âm chuẩn" },
    { icon: "🎮", title: "Mini Game học tập", desc: "Ghép từ, trắc nghiệm, điền từ, kéo thả" },
    { icon: "🔥", title: "Streak hàng ngày", desc: "Duy trì chuỗi học liên tiếp mỗi ngày" },
    { icon: "⚡", title: "Hệ thống XP", desc: "Tích điểm, lên cấp và mở khóa bài học mới" },
    { icon: "🏆", title: "Thành tích & Huy hiệu", desc: "Thu thập huy hiệu khi đạt mốc học tập" },
    { icon: "🗣️", title: "Phát âm TTS", desc: "Nghe người bản ngữ phát âm tiếng Khmer" },
  ];

  const stats = [
    { value: "2,500+", label: "Từ vựng" },
    { value: "50+", label: "Bài học" },
    { value: "10K+", label: "Học viên" },
    { value: "100%", label: "Miễn phí" },
  ];

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: "var(--bg)" }}>
      {/* Decorative background elements for better feel */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-green-400/5 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-blue-400/5 blur-[120px] pointer-events-none" />

      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 max-w-6xl mx-auto relative z-10">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md"
            style={{ background: "linear-gradient(135deg, #0F1B3C 0%, #1a3a6b 50%, #10B981 100%)" }}>
            <svg width="22" height="22" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13 20C13 20 5 16.5 5 9V6C5 6 9 4.5 13 7C17 4.5 21 6 21 6V9C21 16.5 13 20 13 20Z" fill="#F5C842" fillOpacity="0.15" stroke="#F5C842" strokeWidth="1.2" strokeLinejoin="round"/>
              <line x1="13" y1="7" x2="13" y2="20" stroke="#F5C842" strokeWidth="1.2" strokeDasharray="2 1"/>
              <circle cx="8.5" cy="11" r="1" fill="#ffffff" fillOpacity="0.9"/>
              <circle cx="17.5" cy="11" r="1" fill="#ffffff" fillOpacity="0.9"/>
              <circle cx="13" cy="4" r="0.8" fill="#F5C842"/>
              <text x="9" y="17" fontSize="5" fontWeight="bold" fill="#ffffff" fontFamily="Arial">KH</text>
            </svg>
          </div>
          <span className="text-xl font-black" style={{ background: "linear-gradient(135deg, #10B981, #F5C842)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>KH Learn</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login" className="btn-secondary py-2 px-5 text-sm">Đăng nhập</Link>
          <Link to="/register" className="btn-primary py-2 px-5 text-sm">Bắt đầu</Link>
        </div>
      </header>

      {/* Hero */}
      <section className="text-center px-4 sm:px-6 py-8 sm:py-12 md:py-20 max-w-4xl mx-auto relative z-10">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 15 }}
          className="text-6xl sm:text-8xl mb-6 sm:mb-8 animate-float inline-block">🇰🇭</motion.div>

        <motion.h1 initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-3xl sm:text-5xl md:text-7xl font-black mb-4 sm:mb-6 leading-tight tracking-tight">
          Học Tiếng Khmer{" "}
          <br className="hidden sm:block" />
          <span className="bg-gradient-to-r from-green-500 to-green-600 bg-clip-text text-transparent">Dễ & Vui</span>
        </motion.h1>

        <motion.p initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-base sm:text-lg md:text-xl mb-8 sm:mb-10 max-w-2xl mx-auto leading-relaxed px-2" style={{ color: "var(--text-muted)" }}>
          Học tiếng Khmer theo phong cách Duolingo — từ vựng, bài học, mini game và theo dõi tiến độ. Hoàn toàn miễn phí!
        </motion.p>

        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }} className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center px-2">
          <Link to="/register" className="btn-primary text-base sm:text-lg px-8 sm:px-12 py-4 sm:py-5 w-full sm:w-auto shadow-lg shadow-green-500/20">
            Bắt đầu học ngay
          </Link>
          <Link to="/login" className="btn-secondary text-base sm:text-lg px-8 sm:px-12 py-4 sm:py-5 w-full sm:w-auto">
            Tôi đã có tài khoản
          </Link>
        </motion.div>

        {/* Sample khmer word */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
          className="mt-10 sm:mt-16 inline-flex items-center gap-3 sm:gap-4 px-5 sm:px-8 py-4 sm:py-5 rounded-[2rem] shadow-sm cursor-pointer hover:scale-105 transition-transform active:scale-95"
          style={{ background: "rgba(88,204,2,0.08)", border: "2px solid rgba(88,204,2,0.2)" }}
          onClick={() => playTTS("សួស្ដី")}
        >
          <span className="khmer-text text-2xl sm:text-4xl font-bold" style={{ color: "var(--green)" }}>សួស្ដី</span>
          <span className="text-lg sm:text-2xl font-light text-gray-400">|</span>
          <span className="text-lg sm:text-2xl font-black">Xin chào! 👋</span>
        </motion.div>
      </section>

      {/* Stats */}
      <section className="px-4 sm:px-6 py-8 sm:py-12 max-w-5xl mx-auto relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
          {stats.map((stat, i) => (
            <motion.div key={stat.label}
              initial={{ y: 20, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 * i }}
              className="card text-center group hover:border-green-500/50 transition-colors">
              <div className="text-2xl sm:text-4xl font-black mb-1 group-hover:scale-110 transition-transform" style={{ color: "var(--green)" }}>{stat.value}</div>
              <div className="text-[10px] sm:text-xs font-black uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="px-4 sm:px-6 py-12 sm:py-20 max-w-6xl mx-auto relative z-10">
        <h2 className="text-2xl sm:text-4xl font-black text-center mb-8 sm:mb-16 tracking-tight">Tính năng nổi bật 🌟</h2>
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-8">
          {features.map((f, i) => (
            <motion.div key={f.title}
              initial={{ y: 30, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }}
              transition={{ delay: i * 0.1 }} viewport={{ once: true }}
              className="card hover:border-green-400 cursor-default p-4 sm:p-8 group">
              <div className="text-3xl sm:text-5xl mb-3 sm:mb-6 group-hover:scale-125 transition-transform origin-left inline-block">{f.icon}</div>
              <h3 className="text-sm sm:text-xl font-black mb-1 sm:mb-3">{f.title}</h3>
              <p className="text-xs sm:text-sm leading-relaxed hidden sm:block" style={{ color: "var(--text-muted)" }}>{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 sm:px-6 py-12 sm:py-24 text-center relative z-10">
        <motion.div whileInView={{ scale: [0.98, 1], opacity: [0, 1] }} viewport={{ once: true }}
          className="max-w-4xl mx-auto p-8 sm:p-12 md:p-20 rounded-[2rem] sm:rounded-[3rem] shadow-2xl relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, #58CC02 0%, #1CB0F6 100%)" }}>
          {/* Decorative circles inside CTA */}
          <div className="absolute top-0 right-0 w-40 sm:w-64 h-40 sm:h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl" />
          <div className="absolute bottom-0 left-0 w-40 sm:w-64 h-40 sm:h-64 bg-black/10 rounded-full translate-y-1/2 -translate-x-1/2 blur-2xl" />
          
          <div className="relative z-10">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-black text-white mb-4 sm:mb-6 tracking-tight">Sẵn sàng học tiếng Khmer?</h2>
            <p className="text-white/80 mb-6 sm:mb-10 text-base sm:text-xl font-medium max-w-xl mx-auto">Tham gia cùng hàng ngàn học viên đang học mỗi ngày!</p>
            <Link to="/register"
              className="inline-block bg-white font-black text-base sm:text-xl px-8 sm:px-12 py-4 sm:py-5 rounded-2xl transition-all hover:scale-105 active:scale-95 shadow-xl"
              style={{ color: "var(--green)" }}>
              🇰🇭 Bắt đầu ngay
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="text-center py-8 sm:py-12 text-sm relative z-10" style={{ color: "var(--text-muted)", borderTop: "2px solid var(--border)" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row justify-between items-center gap-3 sm:gap-4">
          <p className="font-bold text-xs sm:text-sm">© 2026 KH Learn — Học tiếng Khmer miễn phí 🇰🇭</p>
          <div className="flex gap-4 sm:gap-6 font-bold text-[10px] sm:text-xs uppercase tracking-widest">
            <a href="#" className="hover:text-green-500">Về chúng tôi</a>
            <a href="#" className="hover:text-green-500">Điều khoản</a>
            <a href="#" className="hover:text-green-500">Liên hệ</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
